// Single source of truth for the sidebar menu AND the catalog of assignable
// permissions used by the "Gestão de Perfis e Privilégios" screens. Each leaf
// item's `permissionCode` is what a Profile (role) stores in its
// `permissions` array; MasterLayout filters this same structure by the
// logged-in user's permissions to build the visible sidebar.
//
// `label` holds an i18n key (see src/i18n/translations/{pt,en}.js, `menu.*`
// entries) rather than literal text — consumers must render it through
// useTranslation()'s `t()` so the sidebar and the permission pickers follow
// the active locale.
//
// icon shapes:
//   { kind: 'iconify', name: 'mdi:...' }     -> <Icon icon={name} className='menu-icon' />
//   { kind: 'class', className: 'ri-...' }   -> <i className={`${className} menu-icon`} />
//   { kind: 'bullet', colorClass: '...' }    -> <i className={`ri-circle-fill circle-icon ${colorClass} w-auto`} />

const menuConfig = [
  {
    type: 'link',
    permissionCode: 'dashboard.view',
    label: 'menu.dashboard.view',
    path: '/dashboard',
    icon: { kind: 'iconify', name: 'solar:home-smile-angle-outline' },
  },
  {
    type: 'group',
    key: 'funcionarios',
    label: 'menu.funcionarios',
    icon: { kind: 'class', className: 'ri-briefcase-line' },
    items: [
      { permissionCode: 'funcionarios.cadastrar', label: 'menu.funcionarios.cadastrar', path: '/funcionarios/cadastrar', icon: { kind: 'iconify', name: 'mdi:account-plus-outline' } },
      { permissionCode: 'funcionarios.lista', label: 'menu.funcionarios.lista', path: '/funcionarios/lista', icon: { kind: 'iconify', name: 'mdi:account-multiple-outline' } },
    ],
  },
  {
    type: 'group',
    key: 'professores',
    label: 'menu.professores',
    icon: { kind: 'class', className: 'ri-user-voice-line' },
    items: [
      { permissionCode: 'professores.cadastrar', label: 'menu.professores.cadastrar', path: '/professores/cadastrar', icon: { kind: 'iconify', name: 'mdi:account-plus-outline' } },
      { permissionCode: 'professores.lista', label: 'menu.professores.lista', path: '/professores/lista', icon: { kind: 'iconify', name: 'mdi:account-multiple-outline' } },
    ],
  },
  {
    type: 'group',
    key: 'utilizadores',
    label: 'menu.utilizadores',
    icon: { kind: 'class', className: 'ri-user-settings-line' },
    items: [
      { permissionCode: 'utilizadores.cadastrar', label: 'menu.utilizadores.cadastrar', path: '/utilizadores/cadastrar', icon: { kind: 'iconify', name: 'mdi:account-plus-outline' } },
      { permissionCode: 'utilizadores.lista', label: 'menu.utilizadores.lista', path: '/utilizadores/lista', icon: { kind: 'iconify', name: 'mdi:account-multiple-outline' } },
      { permissionCode: 'utilizadores.perfis', label: 'menu.utilizadores.perfis', path: '/utilizadores/perfis', icon: { kind: 'iconify', name: 'mdi:shield-account-outline' } },
    ],
  },
  {
    type: 'group',
    key: 'estudantes',
    label: 'menu.estudantes',
    icon: { kind: 'class', className: 'ri-graduation-cap-line' },
    items: [
      { permissionCode: 'estudantes.cadastrar', label: 'menu.estudantes.cadastrar', path: '/estudantes/cadastrar', icon: { kind: 'iconify', name: 'mdi:account-plus-outline' } },
      { permissionCode: 'estudantes.activos', label: 'menu.estudantes.activos', path: '/estudantes/activos', icon: { kind: 'iconify', name: 'mdi:account-check-outline' } },
      { permissionCode: 'estudantes.inactivos', label: 'menu.estudantes.inactivos', path: '/estudantes/inactivos', icon: { kind: 'iconify', name: 'mdi:account-off-outline' } },
      { permissionCode: 'estudantes.nao_inscritos', label: 'menu.estudantes.nao_inscritos', path: '/estudantes/nao-inscritos', icon: { kind: 'iconify', name: 'mdi:account-question-outline' } },
      { permissionCode: 'estudantes.notificacoes', label: 'menu.estudantes.notificacoes', path: '/estudantes/notificacoes', icon: { kind: 'iconify', name: 'mdi:bell-outline' } },
    ],
  },
  {
    // Merges the old "Lista de Presenças" that used to live inside
    // Professores (teacher-only) and Estudantes — the teacher-facing one now
    // covers ALL collaborators (teachers + funcionários) in a single list,
    // since routes/attendance.js's GET /:sucursalId already unions
    // TEACHER_TABLE + WORKER_TABLE into one report keyed by workerId. See
    // ListaPresencasColaboradoresLayer.jsx.
    type: 'group',
    key: 'presencas',
    label: 'menu.presencas',
    icon: { kind: 'class', className: 'ri-calendar-check-line' },
    items: [
      { permissionCode: 'presencas.colaboradores', label: 'menu.presencas.colaboradores', path: '/presencas/colaboradores', icon: { kind: 'iconify', name: 'mdi:calendar-account-outline' } },
      { permissionCode: 'presencas.estudantes', label: 'menu.presencas.estudantes', path: '/presencas/estudantes', icon: { kind: 'iconify', name: 'mdi:calendar-check-outline' } },
    ],
  },
  {
    type: 'group',
    key: 'pagamentos',
    label: 'menu.pagamentos',
    icon: { kind: 'class', className: 'ri-wallet-3-line' },
    items: [
      { permissionCode: 'pagamentos.propinas', label: 'menu.pagamentos.propinas', path: '/pagamentos/propinas', icon: { kind: 'iconify', name: 'mdi:currency-usd' } },
      { permissionCode: 'pagamentos.facturacao_agregada', label: 'menu.pagamentos.facturacao_agregada', path: '/pagamentos/facturacao-agregada', icon: { kind: 'iconify', name: 'mdi:file-document-multiple-outline' } },
    ],
  },
  {
    // "Configurações" — parent menu holding "Configurações Gerais" (the old
    // top-level singleLink entry, now nested one level in) and "Auditoria".
    // Both children only show up for whoever the logged-in profile's
    // `permissions` grant: in practice Super Admin (005) and Admin (002)
    // always see them (their profiles carry the '*' wildcard) and
    // Director(a) (004) sees them because sge_infantil_core's permission
    // scripts explicitly grant these codes to that profile — see
    // sge_infantil_core/scripts/seed-legacy-profiles-and-migrate-users.js
    // and add-configuracoes-auditoria-permission.js. No other profile has
    // these codes, so this whole submenu stays hidden for them.
    type: 'group',
    key: 'configuracoes',
    label: 'menu.configuracoes.settings',
    icon: { kind: 'iconify', name: 'mdi:cog-outline' },
    items: [
      {
        // Multas/Pagamentos/Pautas are ConfiguracoesLayer's own internal tabs
        // (src/i18n/translations/{pt,en}.js: config.tab.*), not separate
        // pages — `permissionCodes` (plural) makes this ONE sidebar entry,
        // visible if the user has ANY of the 3, same as the old top-level
        // `singleLink` behaviour it replaces. `subItems` carries the 3 codes
        // + their own paths only so (a) the Perfis e Permissões screen keeps
        // offering them individually and (b) MasterLayout can highlight this
        // link as active from any of the 3 tab routes.
        key: 'configuracoes-gerais',
        label: 'menu.configuracoes.gerais',
        path: '/configuracoes/multas',
        icon: { kind: 'iconify', name: 'mdi:tune-outline' },
        permissionCodes: ['configuracoes.multas', 'configuracoes.pagamentos', 'configuracoes.pautas'],
        subItems: [
          { code: 'configuracoes.multas', label: 'config.tab.multas', path: '/configuracoes/multas' },
          { code: 'configuracoes.pagamentos', label: 'config.tab.pagamentos', path: '/configuracoes/pagamentos' },
          { code: 'configuracoes.pautas', label: 'config.tab.pautas', path: '/configuracoes/pautas' },
        ],
      },
      {
        permissionCode: 'configuracoes.auditoria',
        label: 'menu.configuracoes.auditoria',
        path: '/configuracoes/auditoria',
        icon: { kind: 'iconify', name: 'mdi:clipboard-text-search-outline' },
      },
      {
        permissionCode: 'classes.lista',
        // "Cursos"/"Classes"/"Níveis" — resolved
        // dynamically by MasterLayout via levelKey(labelBase)
        // (src/utils/classesLabels.js). `label` (the "curso" variant) is
        // only a fallback for permissionGroups (Perfis e Permissões isn't
        // scoped to one sucursal).
        label: 'menu.configuracoes.listaCursos.curso',
        labelBase: 'menu.configuracoes.listaCursos',
        dynamicLevelLabel: true,
        path: '/classes/lista',
        icon: { kind: 'iconify', name: 'mdi:certificate-outline' },
      },
      // Moved here from "Pagamentos" — these are catalog/configuration
      // screens (manage extra activities and taxes/emolumentos), not
      // payment actions themselves.
      { permissionCode: 'pagamentos.extras', label: 'menu.pagamentos.extras', path: '/pagamentos/extras', icon: { kind: 'iconify', name: 'mdi:playlist-plus' } },
      { permissionCode: 'pagamentos.taxas', label: 'menu.pagamentos.taxas', path: '/pagamentos/taxas', icon: { kind: 'iconify', name: 'mdi:cash-multiple' } },
    ],
  },
  {
    type: 'group',
    key: 'despesas',
    label: 'menu.despesas',
    icon: { kind: 'class', className: 'ri-file-list-3-line' },
    items: [
      { permissionCode: 'despesas.consumo', label: 'menu.despesas.consumo', path: '/despesas/consumo', icon: { kind: 'iconify', name: 'mdi:chart-donut' } },
      { permissionCode: 'despesas.produtos', label: 'menu.despesas.produtos', path: '/despesas/produtos', icon: { kind: 'iconify', name: 'mdi:package-variant-closed' } },
      { permissionCode: 'despesas.lista', label: 'menu.despesas.lista', path: '/despesas/lista', icon: { kind: 'iconify', name: 'mdi:receipt-text-outline' } },
    ],
  },
  {
    type: 'group',
    key: 'uniformes',
    label: 'menu.uniformes',
    icon: { kind: 'class', className: 'ri-t-shirt-line' },
    items: [
      { permissionCode: 'uniformes.cadastrar', label: 'menu.uniformes.cadastrar', path: '/uniformes/cadastrar', icon: { kind: 'iconify', name: 'mdi:tshirt-crew-outline' } },
      { permissionCode: 'uniformes.stock', label: 'menu.uniformes.stock', path: '/uniformes/stock', icon: { kind: 'iconify', name: 'mdi:warehouse' } },
      { permissionCode: 'uniformes.vendas', label: 'menu.uniformes.vendas', path: '/uniformes/vendas', icon: { kind: 'iconify', name: 'mdi:cart-outline' } },
    ],
  },
  {
    type: 'group',
    key: 'relatorios',
    label: 'menu.relatorios',
    icon: { kind: 'class', className: 'ri-bar-chart-2-line' },
    items: [
      { permissionCode: 'relatorios.pagamentos', label: 'menu.relatorios.pagamentos', path: '/relatorios/pagamentos', icon: { kind: 'iconify', name: 'mdi:cash-check' } },
      { permissionCode: 'relatorios.dividas', label: 'menu.relatorios.dividas', path: '/relatorios/dividas', icon: { kind: 'iconify', name: 'mdi:file-alert-outline' } },
      { permissionCode: 'relatorios.pagamentos_efectuados', label: 'menu.relatorios.pagamentosEfectuados', path: '/relatorios/pagamentos-efectuados', icon: { kind: 'iconify', name: 'mdi:cash-multiple' } },
    ],
  },
  {
    type: 'group',
    key: 'academico',
    label: 'menu.academico',
    icon: { kind: 'iconify', name: 'solar:document-text-outline' },
    items: [
      // Antigo menu "Turmas" (grupo próprio) — passou a viver dentro de
      // Gestão Académica a pedido, mantendo os mesmos permissionCode/path
      // ('turmas.*', '/turmas/...') para não invalidar permissões já
      // atribuídas nem links existentes.
      { permissionCode: 'turmas.criar', label: 'menu.turmas.criar', path: '/turmas/criar', icon: { kind: 'iconify', name: 'mdi:plus-box-outline' } },
      { permissionCode: 'turmas.lista', label: 'menu.turmas.lista', path: '/turmas/lista', icon: { kind: 'iconify', name: 'mdi:google-classroom' } },
      { permissionCode: 'academico.disciplinas', label: 'menu.academico.disciplinas', path: '/academico/disciplinas', icon: { kind: 'iconify', name: 'mdi:book-open-variant-outline' } },
      { permissionCode: 'academico.avaliacoes', label: 'menu.academico.avaliacoes', path: '/academico/avaliacoes', icon: { kind: 'iconify', name: 'mdi:clipboard-list-outline' } },
      { permissionCode: 'academico.lancamento_notas', label: 'menu.academico.lancamento_notas', path: '/academico/lancamento-notas', icon: { kind: 'iconify', name: 'mdi:pencil-plus-outline' } },
      { permissionCode: 'academico.lista_notas', label: 'menu.academico.lista_notas', path: '/academico/lista-notas', icon: { kind: 'iconify', name: 'mdi:format-list-numbered' } },
      { permissionCode: 'academico.pautas', label: 'menu.academico.pautas', path: '/academico/pautas', icon: { kind: 'iconify', name: 'mdi:file-table-outline' } },
      { permissionCode: 'academico.boletim', label: 'menu.academico.boletim', path: '/academico/boletim', icon: { kind: 'iconify', name: 'mdi:certificate-outline' } },
      { permissionCode: 'academico.rendimento_pedagogico', label: 'menu.academico.rendimento_pedagogico', path: '/academico/rendimento-pedagogico', icon: { kind: 'iconify', name: 'mdi:chart-line' } },
    ],
  },
];

// Flat list of every leaf permission, grouped by module — the shape consumed
// by the role-permission assignment screens (one MultiSelectField per group).
// `label` values here are still i18n keys; PermissionGroupsField renders them
// through t().
export const permissionGroups = menuConfig.map((entry) => {
  if (entry.type === 'link') {
    return { key: entry.permissionCode, label: entry.label, items: [{ code: entry.permissionCode, label: entry.label }] };
  }
  return {
    key: entry.key,
    label: entry.label,
    // A "Configurações Gerais"-style item has no single permissionCode of its
    // own (visibility is the OR of `subItems`) — flatten those instead of
    // emitting one bogus {code: undefined} row.
    items: entry.items.flatMap((item) =>
      item.subItems ? item.subItems.map((sub) => ({ code: sub.code, label: sub.label })) : [{ code: item.permissionCode, label: item.label }],
    ),
  };
});

export default menuConfig;
